/* eslint-disable react/prop-types */

import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getReportData } from '../../../../../api/reports';
import CommonPagination from '../../../../../common/components/pagination/pagination';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import {
  downloadTypesEmail,
  endowmentCareSalesPropertyHeader,
  trustedConsumptionItemsHeader,
  trustedSalesItemsHeader,
} from '../../../../../utils/constant';
import { apiErrorHandler, successHandler } from '../../../../../utils/handlers';
import DownloadModal from '../../../../reports/downloadModal/downloadModal';
import EmailModal from '../../../../reports/emailModal/emailModal';
import ItemEmptyTile from '../../../../reports/item-empty-tile/item-empty-tile';
import Toolbar from '../../../../reports/toolbar/toolbar';
import CommonComponentForTableData from './commonComponentForTableData';

export default function TrustMonthlyReportData({ showTable, setShowTable, title, subtitle }) {
  const { page, limit, setPage, setLimit } = usePagination();
  const [loading, setLoading] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const toggleDownloadModal = () => {
    if (showTable?.tableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };
  const toggleEmailModal = () => {
    if (showTable?.tableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const onSerchInputChange = (event) => {
    setPage(1);
    setLimit(5);
    setSearchText(event.target.value.trim());
    debounceApiCall(event.target.value);
  };

  const getUpdatedTableData = async (searchVal) => {
    const requestParams = {
      trustType: showTable.trustType,
      needType: showTable.needType,
      startDate: showTable.fromDate,
      endDate: showTable.toDate,
      timezone: moment.tz.guess(),
      spName: showTable.reportType,
      search: searchVal,
    };
    setLoading(true);
    try {
      const response = await getReportData('trust_monthly_report', requestParams);
      setShowTable({
        ...showTable,
        show: true,
        isSearchResult: true,
        tableData: response.data.data || [],
        totalResults: response.data.data.length || 0,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      apiErrorHandler(error);
    }
  };

  const debounceApiCall = useCallback(
    debounce((val) => {
      getUpdatedTableData(val);
    }, 2000),
    [],
  );

  useEffect(() => {
    setPage(1);
    setLimit(5);
    if (!showTable.isSearchResult) {
      setSearchText('');
    }
  }, [showTable, setLimit, setPage]);

  const onDownloadClick = (response) => {
    successHandler({
      message: {
        message: response.msg,
        title: 'Success',
      },
    });
  };

  useEffect(() => {
    setSlStartNumber(page * limit - limit);
  }, [page, limit]);

  const getPageData = () => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return showTable?.tableData?.slice(startIndex, endIndex);
  };

  const getHeaders = () => {
    switch (showTable.reportType) {
      case 'Trusted_Consumption_Items_With_AT_Changes':
        return trustedConsumptionItemsHeader;
      case 'Trusted_Sales_Item':
        return trustedSalesItemsHeader;
      case 'Endowment_Care_Sales_Property':
        return endowmentCareSalesPropertyHeader;
      default:
        return [];
    }
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <>
      <div className="trustaccounts-toolbar">
        {showTable?.tableData ? (
          <Toolbar
            title={title}
            subTitle={subtitle || `As on Date: ${moment().format('MMM DD,YYYY')}`}
            subSubTitle={`No. of Items: ${showTable.tableData?.length}`}
            isTableDataPresent={showTable.tableData?.length > 0}
            toolTipText={`This report will show all the reports of ${title}`}
            optionOneLabel="Email Report"
            optionOneIcon="MdOutlineEmail"
            onOptionOneClick={toggleEmailModal}
            optionTwoLabel="Download List"
            optionTwoIcon="FiDownload"
            onOptionTwoClick={toggleDownloadModal}
            searchText={searchText}
            isSearchable
            onSerchInputChange={onSerchInputChange}
            SearchInputPlaceholder="Search with Contract Number"
          />
        ) : null}
      </div>
      <div className="trustaccounts-section">
        {showTable?.tableData?.length > 0 ? (
          <>
            <div className="trust-listOfContracts">
              <CommonComponentForTableData
                pageData={getPageData()}
                headers={getHeaders()}
                slStartNumber={slStartNumber}
              />
            </div>
            <div className="pagination-araging-report">
              <CommonPagination
                page={page}
                limit={limit}
                totalResults={showTable?.totalResults}
                limitOptions={[5, 10, 15]}
                setPage={(val) => {
                  setPage(val);
                }}
                setLimit={(val) => {
                  setLimit(val);
                }}
              />
            </div>
            <DownloadModal
              isOpen={isDownloadModalOpen}
              toggle={toggleDownloadModal}
              reportType="trust_monthly_report"
              onDownloadClick={onDownloadClick}
              urlParams={{
                startDate: showTable.fromDate,
                endDate: showTable.toDate,
                timezone: moment.tz.guess(),
                needType: showTable?.needType,
                trustType: showTable?.trustType,
                spName: showTable?.reportType,
              }}
              willReceiveReportFileInEmail
              module="Trust"
            />
            <EmailModal
              isOpen={isEmailModalOpen}
              toggle={toggleEmailModal}
              reportType="trust_monthly_report"
              apiValues={{
                startDate: showTable.fromDate,
                endDate: showTable.toDate,
                timezone: moment.tz.guess(),
                spName: showTable?.reportType,
                needType: showTable?.needType,
                trustType: showTable?.trustType,
              }}
              module="Trust"
              emailOptions={downloadTypesEmail.slice(1)}
              isTrustAccountParams
            />
          </>
        ) : (
          <ItemEmptyTile />
        )}
      </div>
    </>
  );
}
